import _ from 'lodash';
import { action, makeObservable, observable, transaction } from 'mobx';
import moment from 'moment-timezone';

import { errors } from 'helpers';
import { download, webServiceProvider } from 'shared';
import AbstractStore from './abstract/AbstractStore';
import AsyncData from './abstract/AsyncData';
import AppStore from './App';
import IssueObject from './Classes/IssueObject';
import ShopStore from './ShopStore';

import { CurrentUserStore } from 'stores';

import {
  IssuesTableCacheStore,
  PitstopTableCacheStore,
} from 'stores/CacheStore';

const ISSUE_RECOMMEND_ENDPOINT = `${import.meta.env.VITE_ISSUE_RECOMMEND_URL}`;

class IssueStoreC extends AbstractStore {
  getIssueController = new AbortController();
  getIssueSignal = this.getIssueController.signal;
  getIssueTroubleshootingController = new AbortController();
  getRelevantServicesController = new AbortController();

  refetchIssues = observable.box(false);

  fetchPMsController = new AbortController();
  fetchPMsSignal = this.fetchPMsController.signal;

  summaryIssues = new AsyncData();

  currentPMFeedbacks = {
    title: '',
    currentStep: 1,
    pmsToReceiveFeedback: [],
    isModalVisible: false,
  };

  currentPMFeedbacksInputs = [];

  resetCurrentPMFeedbacksInputs = () => {
    this.currentPMFeedbacksInputs = [];
  };

  updateCurrentPMFeedbacksInputs = (index, field, value) => {
    if (!this.currentPMFeedbacksInputs[index]) {
      this.currentPMFeedbacksInputs[index] = {};
    }
    this.currentPMFeedbacksInputs[index] = {
      ...this.currentPMFeedbacksInputs[index],
      [field]: value,
    };
  };

  resetPMFeedbackModalData = () => {
    this.currentPMFeedbacks = {
      title: '',
      currentStep: 1,
      pmsToReceiveFeedback: [],
      isModalVisible: false,
    };
  };

  goToNextStepOnFeedbackModal = () => {
    this.currentPMFeedbacks.currentStep++;
  };

  goToPreviousStepOnFeedbackModal = () => {
    this.currentPMFeedbacks.currentStep--;
  };

  setPMFeedbackModalData = (data) => {
    this.currentPMFeedbacks = data;
  };

  getSummaryForShopId = async (shopId) => {
    shopId = shopId || ShopStore.currentShop.id;
    try {
      const { data } = await webServiceProvider.get(
        `v1/shops/${shopId}/issues/summary`
      );
      transaction(() => {
        for (let i = 0; i < data.length; i++) {
          this.data.set(Number(data[i].id), new IssueObject(data[i]));
          this.summaryIssues.data.push(Number(data[i].id));
        }
        this.summaryIssues.loaded = true;
      });
    } catch (e) {
      this.summaryIssues.setError('Unable to load issues summary for shop');
    }
  };

  returnIssueById = async (id, shopId) => {
    if (!shopId) {
      shopId = ShopStore.currentShop.id;
    }
    try {
      const { data } = await webServiceProvider.getMany(`v1/issues/${id}`, {
        includeHistory: true,
        shopId: shopId,
      });

      transaction(() => {
        this.data.set(Number(data.id), new IssueObject(data));
        this.data.get(Number(data.id)).loaded = true;
      });
      return new IssueObject(data);
    } catch (e) {
      console.error(e);
      // AppStore.addError(`Unable to load issue with id ${id}`);
    }
  };

  getIssueById = async (id, shopId) => {
    if (!shopId) {
      shopId = ShopStore.currentShop.id;
    }
    try {
      const { data } = await webServiceProvider.getMany(`v1/issues/${id}`, {
        includeHistory: true,
        shopId: shopId,
      });

      transaction(() => {
        this.data.set(Number(data.id), new IssueObject(data));
        this.data.get(Number(data.id)).loaded = true;
      });
    } catch (e) {
      console.error(e);
      // AppStore.addError(`Unable to load issue with id ${id}`);
    }
  };
  /**
   *
   * @param {*} id - The issueId
   * @param {*} code - The issue code
   * @param {*} shopId - The shop ID
   * @returns
   */
  getRelevantServices = async (id, code, shopId) => {
    try {
      this.getRelevantServicesController.abort();
      this.getRelevantServicesController = new AbortController();
      const { data } = await webServiceProvider.getMany(
        `v1/issues/${id}/relevant-service-records?code=${code}&shopId=${shopId}`,
        {
          code,
          shopId,
        },
        this.getRelevantServicesController.signal
      );
      transaction(() => {
        this.data.set(String(id) + 'relevant-services', data);
      });
      return data;
    } catch (e) {
      console.error(e);
      // AppStore.addError(`Unable to load relevant service entries for issue with id ${id}`);
    }
  };
  /**
   *
   * @param {*} id - The issueId
   * @param {*} code - The issue code
   * @returns
   */
  getIssueTroubleshooting = async (id, code, fmiCode) => {
    try {
      this.getIssueTroubleshootingController.abort();
      this.getIssueTroubleshootingController = new AbortController();
      const { data } = await webServiceProvider.getMany(
        `v1/issues/${id}/troubleshooting`,
        {
          code,
          fmiCode,
        },
        this.getIssueTroubleshootingController.signal
      );
      transaction(() => {
        this.data.set(
          Number(String(id) + String(code) + String(fmiCode)),
          data
        );
      });

      return data;
    } catch (e) {
      console.error(e);
      // AppStore.addError(`Unable to load issue troubleshooting with id ${id}`);
    }
  };

  addIssueToStore = (id, issue, isReformated) => {
    if (!this.data.has(Number(id)) || isReformated) {
      this.data.set(
        Number(id),
        issue instanceof IssueObject ? issue : new IssueObject(issue)
      );
    }
  };

  getIssuesForShopId = async (
    params,
    store,
    shopId = ShopStore.currentShop.id,
    startDate,
    endDate
  ) => {
    return await this.getIssuesForTable(
      params,
      store,
      `v1/shops/${shopId}/issues`,
      startDate,
      endDate
    );
  };

  downloadIssuesCsv = async (params, shopId) => {
    try {
      const response = await webServiceProvider.fetchCSV(
        `v1/shops/${shopId}/issues/download`,
        params
      );
      download(response, 'issues.csv', 'text/csv');
    } catch (e) {
      throw Error('Unable to download issues!');
    }
  };

  getIssuesForCarId = async (
    params,
    store,
    carId,
    reformatFunc = false,
    sendAllData = false
  ) => {
    const url = `v1/cars/${carId}/issues`;
    const result = await this.getIssuesForTable(
      params,
      store,
      url,
      undefined, // startDate
      undefined, // endDate
      sendAllData
    );

    return Array.from(store.data)
      .map((id) => this.data.get(id))
      .filter(Boolean);
  };

  getPMsAndAssetsByShop = async (params, shopId) => {
    try {
      const { data } = await webServiceProvider.getMany(
        `v1/shops/${shopId}/issues/pms`,
        params
      );
      return data;
    } catch (e) {
      AppStore.addError('Unable to load PMs and assets');
    }
  };

  updatePMService = async (
    shopId,
    serviceId,
    serviceCustomizedId,
    payload = {}
  ) => {
    try {
      const { data } = await webServiceProvider.patch(
        `v1/shops/${shopId}/issues/pms?serviceId=${serviceId}&serviceCustomizedId=${serviceCustomizedId}`,
        payload
      );
      return data;
    } catch (e) {
      console.error(e);
    }
  };

  getIssuesForTable = async (
    {
      offset,
      limit,
      filter,
      sort,
      includeHistory = false,
      shopId,
      dontCheckPms = false,
    } = {},
    tableStore,
    URL,
    startDate,
    endDate,
    sendAllData = false
  ) => {
    try {
      if (!shopId) {
        shopId = ShopStore.currentShop.id;
      }
      tableStore.pending = true;
      tableStore.loaded = false;

      offset = offset || tableStore.offset;
      limit = limit || tableStore.limit;
      filter = filter || tableStore.filter;
      sort = sort || tableStore.sort;

      let params = {
        shopId,
        offset,
        limit,
        includeHistory,
        dontCheckPms,
        ...filter,
        startDate,
        endDate,
      };

      if (params.startDate && moment(params.startDate).isValid()) {
        params.startDate = moment(params.startDate)
          .tz(CurrentUserStore.user.settings.timezone || 'America/Toronto')
          .startOf('day')
          .format();
      }

      if (params.endDate && moment(params.endDate).isValid()) {
        params.endDate = moment(params.endDate)
          .tz(CurrentUserStore.user.settings.timezone || 'America/Toronto')
          .endOf('day')
          .format();
      }

      if (sort) {
        params.sort = sort;
      }

      let paramKeys = _.keys(params);

      let _params = {};

      _.forEach(paramKeys, (key) => {
        if (!_.isNil(params[key]) && params[key] !== '') {
          _params[key] = params[key];
        }
      });
      const { data, meta } = await webServiceProvider.getMany(URL, _params);
      tableStore.data = [];
      _.forEach(data, (item) => {
        this.data.set(item.id, new IssueObject(item));
        if (sendAllData) {
          tableStore.data.push(new IssueObject(item));
        } else {
          tableStore.data.push(item.id);
        }
      });

      tableStore.setDataMetadata(
        meta.total,
        meta.pagination.offset,
        meta.pagination.limit,
        sort,
        filter
      );
    } catch (err) {
      errors.response(err, 'Unable to load services!');
    }
  };

  getPmsForNewTable = async (
    {
      offset,
      limit,
      filter,
      sort,
      includeHistory = false,
      shopId,
      search,
      safetyInspection,
    } = {},
    carId, // make it optional so user can fetch by shopId for service interval page
    startDate,
    endDate,
    sendAllData = false
  ) => {
    try {
      // Set shopId to current shop's id if not provided
      if (!shopId) {
        shopId = ShopStore.currentShop.id;
      }

      // Initialize default values if not provided
      offset = offset || 0;
      limit = limit || 20; // Set a default limit
      filter = filter || {};
      sort = sort || null;
      search = search || null;

      // Construct the params object
      let params = {
        shopId,
        offset,
        limit,
        includeHistory,
        ...filter,
        startDate,
        endDate,
        search,
      };

      if (safetyInspection) {
        params.safetyInspection = safetyInspection || false;
      }

      if (!safetyInspection && carId) {
        params.safetyInspection = safetyInspection || false;
      }

      // Format startDate and endDate to the proper timezone
      if (params.startDate && moment(params.startDate).isValid()) {
        params.startDate = moment(params.startDate)
          .tz(CurrentUserStore.user.settings.timezone || 'America/Toronto')
          .startOf('day')
          .format();
      }

      if (params.endDate && moment(params.endDate).isValid()) {
        params.endDate = moment(params.endDate)
          .tz(CurrentUserStore.user.settings.timezone || 'America/Toronto')
          .endOf('day')
          .format();
      }

      // Include sorting if applicable
      if (sort) {
        params.sort = sort;
      }

      if (search) {
        params.search = search;
      }

      // Clean up params by removing undefined or empty values
      let paramKeys = _.keys(params);
      let _params = {};
      _.forEach(paramKeys, (key) => {
        if (!_.isNil(params[key]) && params[key] !== '') {
          _params[key] = params[key];
        }
      });

      if (this.fetchPMsController) {
        this.fetchPMsController.abort('Aborting previous request');
      }

      this.fetchPMsController = new AbortController();
      this.fetchPMsSignal = this.fetchPMsController.signal;

      // Fetch data using the web service provider and pass the constructed params
      const result = carId
        ? await webServiceProvider.getMany(
            `v1/cars/${carId}/pms`,
            _params,
            this.fetchPMsSignal
          )
        : await webServiceProvider.getMany(
            `v1/cars/service-interval/${shopId}/`,
            _params,
            this.fetchPMsSignal
          );

      return result;
    } catch (err) {
      if (err !== 'Aborting previous request') {
        console.log(err);
        errors.response(err, 'Unable to load preventive maintenance data!');
      }

      return null; // Return null in case of error
    }
  };

  getPmForRecomendation = async (carId, shopId) => {
    try {
      // Construct the API URL
      const URL = `v1/cars/${carId}/pms`;

      // Define the query parameters
      const params = {
        shopId,
        offset: 0,
        limit: 50,
        includeHistory: false,
        status: 'new',
        source: 'routine',
        startDate: false,
        endDate: false,
        safetyInspection: false,
      };

      // Make the API request
      const { data } = await webServiceProvider.getMany(URL, params);

      // Return the fetched data
      return data;
    } catch (err) {
      console.error('Error fetching PMs for recommendation:', err);
      return null; // Return null in case of an error
    }
  };

  getIssuesRecommendation = async (issues) => {
    try {
      const URL = ISSUE_RECOMMEND_ENDPOINT;
      const data = await webServiceProvider.directFetchWithMethod(URL, 'POST', {
        issues,
      });

      // Return the response data
      return data;
    } catch (error) {
      console.error('Error in getIssuesRecommendation:', error);
      return null;
    }
  };

  removeSelectedServices = async (selectedRows) => {
    try {
      const serviceHashes = selectedRows.map((row) => row.hash); // Use hash instead of id

      // Iterate over each service and send a delete request
      for (let serviceHash of serviceHashes) {
        // URL-encode the hash to prevent special character issues
        const encodedHash = encodeURIComponent(serviceHash);

        // Make the delete request with the encoded hash
        await webServiceProvider.delete(`v1/cars/pms/${encodedHash}`); // Use the encoded hash in your API request
      }

      // After the removal, update the local store
      transaction(() => {
        for (let serviceHash of serviceHashes) {
          this.data.delete(serviceHash); // Use hash (string), not Number
        }
      });
    } catch (error) {
      console.error('Error removing selected services:', error);
      throw error; // Throw error so it can be handled in the calling component
    }
  };

  clearCacheForIssuesCarFromOtherTabs = () => {
    PitstopTableCacheStore.reset();
    IssuesTableCacheStore.reset();
  };

  setRefetchIssues(value) {
    this.refetchIssues = value;
  }

  getEscalatedDetails = async (issueId) => {
    try {
      const URL = `v1/issues-escalate/${issueId}`;
      const { data } = await webServiceProvider.getMany(URL);
      return data;
    } catch (error) {
      console.error('Error fetching escalation details:', error);
      return null;
    }
  };

  getCarIssuesList = async (
    { offset, limit, filter, sort, includeHistory = false } = {},
    carId
  ) => {
    try {
      const URL = `v1/cars/${carId}/issues`;

      let params = {
        offset,
        limit,
        includeHistory,
        ...filter,
      };

      if (sort) {
        params.sort = sort;
      }

      let paramKeys = _.keys(params);

      let _params = {};

      _.forEach(paramKeys, (key) => {
        if (!_.isNil(params[key]) && params[key] !== '') {
          _params[key] = params[key];
        }
      });

      return webServiceProvider.getMany(URL, _params).then((r) => r.data);
    } catch (err) {
      errors.response(err, 'Unable to load services!');
    }
  };

  reset = () => {
    this.data.clear();
    this.summaryIssues.reset();
  };
  constructor() {
    super();
    makeObservable(this, {
      currentPMFeedbacks: observable,
      currentPMFeedbacksInputs: observable,
      getIssuesForShopId: action,
      getIssuesForTable: action,
      addIssueToStore: action,
      setPMFeedbackModalData: action,
      resetPMFeedbackModalData: action,
      reset: action,
      refetchIssues: observable,
    });
  }
}

const IssueStore = new IssueStoreC();

export default IssueStore;
